<template>
  <BorderedTile
    :excluded="excluded"
    :menu-actions="menuActions"
    :menu-title="menuTitle"
    :selected="selected"
    @click="$emit('click', file)"
    @click:action="$emit('click:action', { action: $event.action, file })"
  >
    <v-img
      v-if="previewSrc"
      :alt="name"
      aspect-ratio="1"
      cover
      :src="previewSrc"
      @load="revokeTemporarySrc()"
    >
      <template #placeholder>
        <div class="img-placeholder-bg d-flex align-center justify-center fill-height">
          <v-progress-circular
            color="grey-lighten-4"
            indeterminate
          />
        </div>
      </template>
    </v-img>

    <vi-icon
      v-else
      class="file-type-icon"
      :icon="icon"
      :size="75"
    />

    <template #caption>
      <vi-icon
        class="caption-icon"
        :icon="icon"
      />
      {{ name }}

      <v-tooltip
        activator="parent"
        location="top"
        :text="description"
      />
    </template>
  </BorderedTile>
</template>

<script setup lang="ts">
const props = defineProps<{
  file: File | MediaAsset
  excluded?: boolean
  selected?: boolean
  menuActions?: ButtonConfigMap
  menuTitle?: string
}>();
defineEmits(['click', 'click:action']);
const fileRef = toRef(props, 'file');
const previewSrc = ref<string>();
const typePrefix = computed(() => getFileTypePrefix(fileRef.value));
const icon = computed(() => {
  const icons: { [key: string]: string } = {
    image: 'mdi:image-outline',
    video: 'mdi:video-outline',
    text: 'mdi:file-document-outline',
    default: 'mdi:paperclip',
  };
  return icons[typePrefix.value] || icons.default;
});
const name = computed(() =>
  isFile(fileRef.value)
    ? fileRef.value.name
    : fileRef.value.title,
);
const description = computed(() =>
  isFile(fileRef.value)
    ? fileRef.value.name
    : fileRef.value.description,
);

onMounted(async () => {
  if (isFile(props.file)) {
    if (props.file.type.startsWith('image/')) {
      previewSrc.value = URL.createObjectURL(props.file);
    }
  }
  else {
    if (props.file.asset_type === 'image') {
      const { getMediaAssetBlob } = useApi();
      const preview = await getMediaAssetBlob(props.file.id);
      previewSrc.value = URL.createObjectURL(preview);
    }
  }
});

/** Revokes the temporary url for the file to free up memory */
const revokeTemporarySrc = () => {
  if (isFile(props.file)) {
    URL.revokeObjectURL(previewSrc.value!);
  }
};
</script>

<style scoped>
.img-placeholder-bg {
  background: rgba(0, 0, 0, 0.2);
}

.file-type-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.caption-icon {
  transform: translateY(0px);
}
</style>
